import React, { useEffect, useState } from 'react';
import type { AnswerOption } from '../types';
import AnswerOptions from './AnswerOptions';
import ChoiceButtons from './ChoiceButtons';

const shakeKeyframes = `
@keyframes bubble-shake {
  0%   { transform: translateX(0); }
  15%  { transform: translateX(-8px); }
  30%  { transform: translateX(7px); }
  45%  { transform: translateX(-6px); }
  60%  { transform: translateX(5px); }
  75%  { transform: translateX(-3px); }
  90%  { transform: translateX(2px); }
  100% { transform: translateX(0); }
}
.answer-bubble-content::-webkit-scrollbar {
  width: 4px;
}
.answer-bubble-content::-webkit-scrollbar-thumb {
  background: rgba(20, 184, 166, 0.35);
  border-radius: 4px;
}
`;

interface AnswerBubbleProps {
  options: AnswerOption[];
  onChoose: (optionId: string) => void;
  showOptions: boolean;
  isResolving: boolean;
  chosenId?: string | null;
  visible?: boolean;
}

const AnswerBubble: React.FC<AnswerBubbleProps> = ({
  options,
  onChoose,
  showOptions,
  isResolving,
  chosenId,
  visible = true,
}) => {
  const [shaking, setShaking] = useState(false);

  const chosenOption = chosenId ? options.find((o) => o.id === chosenId) : null;
  const chosenIsWrong = isResolving && chosenOption && chosenOption.isCorrect === false;

  useEffect(() => {
    if (!chosenIsWrong) return;
    setShaking(true);
    const t = window.setTimeout(() => setShaking(false), 600);
    return () => clearTimeout(t);
  }, [chosenIsWrong, chosenId]);

  if (!options.length) return null;

  return (
    <>
      <style>{shakeKeyframes}</style>
      <div
        className={`transition-opacity duration-300 ease-in-out ${
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          width: 'clamp(280px, min(70vw, 90vh), 720px)',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          animation: shaking ? 'bubble-shake 0.6s ease' : undefined,
          pointerEvents: 'auto',
        }}
      >
        {/* Speech Bubble */}
        <div
          className="relative w-full flex flex-col justify-center"
          style={{
            minHeight: 0,
            borderRadius: '20px',
            border: '1px solid rgba(0,0,0,0.1)',
            background: 'rgba(255, 255, 255, 0.92)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            overflow: 'visible',
            fontFamily: 'Inter, system-ui, sans-serif',
            padding: 'clamp(4px, 1.2vh, 16px) clamp(8px, 1.2vw, 20px)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '-18px',
              right: '40px',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderTop: '18px solid rgba(255, 255, 255, 0.92)',
              filter: 'drop-shadow(0 3px 2px rgba(0,0,0,0.1))',
            }}
          />

          <div className="relative z-10 w-full answer-bubble-content min-h-0 overflow-y-auto pr-1">
            {showOptions && (
              options.length > 2 ? (
                <AnswerOptions options={options} onChoose={onChoose} visible={visible} />
              ) : (
                <ChoiceButtons
                  options={options as [AnswerOption, AnswerOption]}
                  onChoose={onChoose}
                />
              )
            )}

            {isResolving && (
              options.length > 2 ? (
                <AnswerOptions
                  options={options}
                  onChoose={() => {}}
                  disabled
                  chosenId={chosenId}
                  showResult
                  visible={visible}
                />
              ) : (
                <ChoiceButtons
                  options={options as [AnswerOption, AnswerOption]}
                  onChoose={() => {}}
                  disabled
                  chosenId={chosenId}
                  showResult
                />
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnswerBubble;